import { useGoogleLogin } from '@react-oauth/google';
import { useMutation } from '@tanstack/react-query';
import { notification } from 'antd';
import { checkAccessApi, apiCall as oauthApiCall } from 'api-calls/auth';
import {
  EmailOtpSendResponse,
  enableAuthenticatorApi,
  sendEmailOtpApi,
  setupAuthenticatorApi,
  signInErrorMessage,
  signInMethodApi,
  StaffSessionResponse,
  verifyAuthenticatorApi,
  verifyEmailOtpApi,
} from 'api-calls/sign-in';
import loginApi from 'api-calls/login';
import {
  AdminLoginResponse,
  errorCodeOf,
  loginEnableTwoFactorApi,
  loginSetupTwoFactorApi,
  loginVerifyTwoFactorApi,
  TwoFactorSetup,
  twoFactorErrorMessage,
} from 'api-calls/two-factor';
import BackupCodes from 'apps/home/components/two-factor/BackupCodes';
import TwoFactorCodeInput from 'apps/home/components/two-factor/TwoFactorCodeInput';
import TwoFactorSetupForm from 'apps/home/components/two-factor/TwoFactorSetupForm';
import { Button } from 'common/ui/button';
import { Input } from 'common/ui/input';
import { PasswordInput } from 'common/ui/password-input';
import { useAuth } from 'context/auth-context';
import jwtDecode from 'jwt-decode';
import { useEffect, useState } from 'react';
import {
  deleteToken,
  hasStaffSession,
  setAccessToken,
  setStaffSession,
  setUser,
} from 'utils/auth';

/*{ at_hash: "tQjOjzkdIbubDaN7ONUveg",
  aud: "247020062444-6gmieg3e3rmtr1gfa3f2492lsogv34fr.apps.googleusercontent.com",
  azp: "247020062444-6gmieg3e3rmtr1gfa3f2492lsogv34fr.apps.googleusercontent.com",
  email: "vu@stag.vn",
  email_verified: true,
  exp: 1768454106,
  family_name: "Nguyen",
  given_name: "Vu",
  hd: "stag.vn",
  iat: 1768450506,
  iss: "https://accounts.google.com",
  name: "Vu Nguyen",
  picture: "https://lh3.googleusercontent.com/a/ACg8ocIITPuHOWg9hhoD4x2O6ty2y2Hp5i8LvfBOXsf0zDeiZUjFAw=s96-c",
  sub: "114634537955316811263"
*/
type GoogleUser = {
  at_hash: string;
  aud: string;
  azp: string;
  email: string;
  email_verified: boolean;
  exp: number;
  family_name: string;
  given_name: string;
  hd: string;
  iat: number;
  iss: string;
  name: string;
  picture: string;
  sub: string;
};

type StaffAccount = {
  id: string;
  name: string;
  email: string;
  email_verified: boolean;
  photo: string;
  created_at: string;
  updated_at: string;
};

type LoginStep =
  | 'password'
  | 'code'
  | 'setup'
  | 'backup-codes'
  // after Google sign-in: the authenticator if set up, else a code by email
  | 'google-checking'
  | 'google-authenticator'
  | 'google-email'
  // roles that require an authenticator and have none
  | 'google-setup'
  | 'google-backup-codes';

const redirectAfterLogin = () => {
  const searchParams = new URLSearchParams(window.location.search);
  window.location.href = searchParams.get('url') || '/';
};

// Email/password login is turned off for now; staff sign in with Google only.
// Flip back to true to bring the password form (and its 2FA steps) back.
const PASSWORD_LOGIN_ENABLED = false;

const AdminLoginPage = () => {
  const { user, isAuthenticated } = useAuth();

  const [step, setStep] = useState<LoginStep>('password');

  // Google sign-in, then a second code: from the authenticator app if the
  // staff member set one up, otherwise by email. The Google tokens are
  // stored right away (the second step needs them), the user only once
  // there is a staff session.
  const [googleUser, setGoogleUser] = useState<StaffAccount | null>(null);
  const [googleCode, setGoogleCode] = useState('');
  const [googleError, setGoogleError] = useState('');
  const [otpSent, setOtpSent] = useState<EmailOtpSendResponse | null>(null);
  const [googleSetup, setGoogleSetup] = useState<TwoFactorSetup | null>(null);
  // Signed in with the authenticator just set up: kept until the backup
  // codes are saved.
  const [
    pendingSession,
    setPendingSession,
  ] = useState<StaffSessionResponse | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const finishGoogleLogin = (session?: StaffSessionResponse) => {
    if (!session?.session_token || !googleUser) {
      setGoogleError('Không lấy được phiên đăng nhập, vui lòng thử lại.');
      return;
    }
    setStaffSession(session.session_token, session.expires_at);
    setUser(googleUser);
    redirectAfterLogin();
  };

  const cancelGoogleLogin = () => {
    deleteToken();
    setGoogleUser(null);
    setGoogleCode('');
    setGoogleError('');
    setOtpSent(null);
    setGoogleSetup(null);
    setPendingSession(null);
    setStep('password');
  };

  const errorMessage = (err: any) =>
    signInErrorMessage(err, twoFactorErrorMessage);

  const { mutate: sendOtp, isPending: isSendingOtp } = useMutation({
    mutationFn: () => sendEmailOtpApi.queryFn(undefined),
    onSuccess: (response: any) => {
      setOtpSent(response?.data || null);
      setGoogleError('');
      setNow(Date.now());
    },
    onError: (err: any) => setGoogleError(errorMessage(err)),
  });

  const {
    mutate: startGoogleSetup,
    isPending: isStartingGoogleSetup,
  } = useMutation({
    mutationFn: () => setupAuthenticatorApi.queryFn(undefined),
    onSuccess: (response: any) => setGoogleSetup(response?.data || null),
    onError: (err: any) => setGoogleError(errorMessage(err)),
  });

  const {
    mutate: enableGoogleAuthenticator,
    isPending: isEnablingGoogle,
  } = useMutation({
    mutationFn: (code: string) => enableAuthenticatorApi.queryFn({ code }),
    onSuccess: (response: any) => {
      const session: StaffSessionResponse | undefined = response?.data;
      if (session?.backup_codes?.length) {
        setPendingSession(session);
        setStep('google-backup-codes');
        return;
      }
      finishGoogleLogin(session);
    },
    onError: (err: any) => setGoogleError(errorMessage(err)),
  });

  const { mutate: checkMethod } = useMutation({
    mutationFn: () => signInMethodApi.queryFn(undefined),
    onSuccess: (response: any) => {
      setGoogleError('');
      if (response?.data?.method === 'authenticator') {
        setStep('google-authenticator');
        return;
      }
      if (response?.data?.method === 'setup') {
        setStep('google-setup');
        startGoogleSetup();
        return;
      }
      setStep('google-email');
      sendOtp();
    },
    onError: (err: any) => {
      notification.open({ type: 'error', message: errorMessage(err) });
      cancelGoogleLogin();
    },
  });

  const onCodeError = (err: any) => {
    setGoogleCode('');
    setGoogleError(errorMessage(err));
  };

  const {
    mutate: verifyAuthenticator,
    isPending: isVerifyingAuthenticator,
  } = useMutation({
    mutationFn: (code: string) => verifyAuthenticatorApi.queryFn({ code }),
    onSuccess: (response: any) => finishGoogleLogin(response?.data),
    onError: onCodeError,
  });

  const { mutate: verifyOtp, isPending: isVerifyingOtp } = useMutation({
    mutationFn: (code: string) => verifyEmailOtpApi.queryFn({ code }),
    onSuccess: (response: any) => finishGoogleLogin(response?.data),
    onError: onCodeError,
  });

  // Countdown for "send again".
  useEffect(() => {
    if (step !== 'google-email') return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [step]);

  // Google's refresh token stays on the server; the response only carries the
  // ID token. With a staff session still live for this account, the second
  // factor is skipped.
  const { mutate: googleLogin, isPending: isGoogleLoading } = useMutation({
    mutationFn: async (code: string) => {
      const response: any = await oauthApiCall.queryFn({ code });
      const { id_token, expires_in } = response || {};
      if (!id_token) return { response, sessionLive: false };
      setAccessToken(id_token, expires_in);
      let sessionLive = false;
      if (hasStaffSession()) {
        try {
          await checkAccessApi.queryFn(undefined);
          sessionLive = true;
        } catch {
          // ended, or belongs to another account: second factor again
        }
      }
      return { response, sessionLive };
    },
    onSuccess: ({ response, sessionLive }: any) => {
      const { id_token } = response || {};
      if (!id_token) {
        notification.open({
          type: 'error',
          message: 'Không lấy được token',
        });
        return;
      }

      // decrypt id_token to get user info
      const user: GoogleUser = jwtDecode(id_token);
      const staffUser = {
        id: user.sub,
        name: user.name,
        email: user.email,
        email_verified: user.email_verified,
        photo: user.picture,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } as StaffAccount;
      if (sessionLive) {
        setUser(staffUser);
        redirectAfterLogin();
        return;
      }
      // Drop a stale session so the second factor starts clean.
      localStorage.removeItem('adminSession.staffSession');
      setGoogleUser(staffUser);
      setGoogleCode('');
      setGoogleError('');
      setOtpSent(null);
      setStep('google-checking');
      checkMethod();
    },
    onError: (error: any) => {
      notification.open({
        type: 'error',
        message:
          error?.status === 403
            ? 'Tài khoản Google này không có quyền truy cập Stag Admin.'
            : `Lỗi: ${error?.response?.error ||
                error?.message ||
                'Google login failed'}`,
      });
    },
  });

  const handleGoogleCodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = googleCode.trim();
    if (!value) return;
    setGoogleError('');
    if (step === 'google-authenticator') {
      verifyAuthenticator(value);
    } else if (value.length === 6) {
      verifyOtp(value);
    }
  };

  // Email/password login, then (when 2FA is on, or required but not set up)
  // a second step with the login challenge it returns.
  const [loginChallenge, setLoginChallenge] = useState('');
  const [twoFactorSetup, setTwoFactorSetup] = useState<TwoFactorSetup | null>(
    null
  );
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  // Signed in with 2FA just turned on: kept until the backup codes are saved.
  const [pendingLogin, setPendingLogin] = useState<AdminLoginResponse | null>(
    null
  );

  // Email/password state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const finishLogin = (data?: AdminLoginResponse) => {
    const { staff_account, access_token } = (data || {}) as AdminLoginResponse;
    if (!access_token) {
      notification.open({
        type: 'error',
        message: 'Không lấy được token',
      });
      return;
    }
    setAccessToken(access_token);
    setUser(staff_account as StaffAccount);

    const searchParams = new URLSearchParams(window.location.search);
    const redirectUrl = searchParams.get('url') || '/';
    window.location.href = redirectUrl;
  };

  const restart = () => {
    setStep('password');
    setLoginChallenge('');
    setTwoFactorSetup(null);
    setCode('');
    setCodeError('');
    setPassword('');
  };

  // Shared error handling for the second step: an expired/unknown challenge
  // sends the user back to email/password.
  const handleTwoFactorError = (err: any) => {
    if (errorCodeOf(err) === 'invalid_login_challenge') {
      notification.open({
        type: 'error',
        message: twoFactorErrorMessage(err),
      });
      restart();
      return;
    }
    setCodeError(twoFactorErrorMessage(err));
  };

  const { mutate: startSetup, isPending: isStartingSetup } = useMutation({
    mutationFn: (challenge: string) =>
      loginSetupTwoFactorApi.queryFn({ login_challenge: challenge }),
    onSuccess: (response: any) => {
      setTwoFactorSetup(response?.data || null);
    },
    onError: handleTwoFactorError,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (params: any) => loginApi.queryFn(params),
    onSuccess: (response: any) => {
      const data: AdminLoginResponse = response?.data || {};
      if (data.two_factor_required && data.login_challenge) {
        setLoginChallenge(data.login_challenge);
        setCode('');
        setCodeError('');
        setStep('code');
        return;
      }
      if (data.two_factor_setup_required && data.login_challenge) {
        setLoginChallenge(data.login_challenge);
        setTwoFactorSetup(null);
        setCodeError('');
        setStep('setup');
        startSetup(data.login_challenge);
        return;
      }
      finishLogin(data);
    },
    onError: (error: any) => {
      notification.open({
        type: 'error',
        message:
          error?.status === 429
            ? 'Bạn thao tác quá nhanh, vui lòng thử lại sau ít phút.'
            : `Lỗi: ${error?.response?.error ||
                error?.response?.error_code ||
                'Login failed'}`,
      });
    },
  });

  const { mutate: verifyCode, isPending: isVerifying } = useMutation({
    mutationFn: (params: { login_challenge: string; code: string }) =>
      loginVerifyTwoFactorApi.queryFn(params),
    onSuccess: (response: any) => finishLogin(response?.data),
    onError: handleTwoFactorError,
  });

  const { mutate: enableTwoFactor, isPending: isEnabling } = useMutation({
    mutationFn: (params: { login_challenge: string; code: string }) =>
      loginEnableTwoFactorApi.queryFn(params),
    onSuccess: (response: any) => {
      const data: AdminLoginResponse = response?.data || {};
      if (data.backup_codes?.length) {
        setPendingLogin(data);
        setStep('backup-codes');
        return;
      }
      finishLogin(data);
    },
    onError: handleTwoFactorError,
  });

  // Google login
  const login = useGoogleLogin({
    onSuccess: res => googleLogin(res.code),
    flow: 'auth-code',
    onError: error => console.log('useGoogleLoginError', error),
  });

  // Email/password login handler
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutate({ email, password });
  };

  const handleCodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = code.trim();
    if (!value) return;
    setCodeError('');
    verifyCode({ login_challenge: loginChallenge, code: value });
  };

  if (user && isAuthenticated) {
    return <div>Redirecting...</div>;
  }

  if (step === 'google-checking') {
    return (
      <LoginCard title="Đăng nhập">
        <div className="text-center text-gray-500">Đang kiểm tra...</div>
      </LoginCard>
    );
  }

  if (step === 'google-authenticator') {
    return (
      <LoginCard title="Xác thực hai lớp">
        <form onSubmit={handleGoogleCodeSubmit} className="space-y-5">
          <p className="text-sm text-gray-600">
            Nhập mã 6 số trong ứng dụng xác thực, hoặc một mã dự phòng.
          </p>
          <div>
            <label className="block mb-1 font-medium">Mã xác thực</label>
            <TwoFactorCodeInput
              value={googleCode}
              onChange={setGoogleCode}
              allowBackupCode
              disabled={isVerifyingAuthenticator}
            />
          </div>
          {googleError && (
            <div className="text-red-500 text-sm">{googleError}</div>
          )}
          <Button
            type="submit"
            className="w-full"
            disabled={isVerifyingAuthenticator || !googleCode.trim()}
          >
            {isVerifyingAuthenticator ? 'Đang xác thực...' : 'Xác thực'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={cancelGoogleLogin}
            disabled={isVerifyingAuthenticator}
          >
            Đăng nhập bằng tài khoản khác
          </Button>
          <p className="text-xs text-gray-500">
            Mất điện thoại và hết mã dự phòng? Nhờ quản trị viên đặt lại xác
            thực hai lớp; sau đó bạn sẽ đăng nhập bằng mã gửi qua email.
          </p>
        </form>
      </LoginCard>
    );
  }

  if (step === 'google-setup') {
    return (
      <LoginCard title="Thiết lập xác thực hai lớp">
        <p className="mb-4 text-sm text-gray-600">
          Vai trò của bạn bắt buộc dùng ứng dụng xác thực (Google Authenticator,
          1Password...). Quét mã QR rồi nhập mã 6 số để tiếp tục đăng nhập.
        </p>
        {googleSetup ? (
          <TwoFactorSetupForm
            setup={googleSetup}
            onSubmit={value => {
              setGoogleError('');
              enableGoogleAuthenticator(value);
            }}
            isPending={isEnablingGoogle}
            error={googleError}
            submitLabel="Bật và đăng nhập"
            onCancel={cancelGoogleLogin}
          />
        ) : (
          <div className="space-y-4">
            {isStartingGoogleSetup ? (
              <div className="text-center text-gray-500">Đang tải...</div>
            ) : (
              <>
                {googleError && (
                  <div className="text-red-500 text-sm">{googleError}</div>
                )}
                <Button
                  type="button"
                  className="w-full"
                  onClick={() => startGoogleSetup()}
                >
                  Thử lại
                </Button>
              </>
            )}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={cancelGoogleLogin}
              disabled={isStartingGoogleSetup}
            >
              Quay lại
            </Button>
          </div>
        )}
      </LoginCard>
    );
  }

  if (step === 'google-backup-codes' && pendingSession) {
    return (
      <LoginCard title="Mã dự phòng">
        <BackupCodes
          codes={pendingSession.backup_codes || []}
          onDone={() => finishGoogleLogin(pendingSession)}
          doneLabel="Tiếp tục đăng nhập"
        />
      </LoginCard>
    );
  }

  if (step === 'google-email') {
    const resendIn = otpSent
      ? Math.max(
          0,
          Math.ceil((new Date(otpSent.resend_after).getTime() - now) / 1000)
        )
      : 0;
    return (
      <LoginCard title="Xác thực email">
        <form onSubmit={handleGoogleCodeSubmit} className="space-y-5">
          <p className="text-sm text-gray-600">
            {otpSent ? (
              <>
                Chúng tôi đã gửi mã 6 số đến <b>{otpSent.email}</b>. Mã có hiệu
                lực trong 5 phút.
              </>
            ) : isSendingOtp ? (
              'Đang gửi mã xác thực đến email của bạn...'
            ) : (
              'Chưa gửi được mã xác thực.'
            )}
          </p>
          <div>
            <label className="block mb-1 font-medium">Mã xác thực</label>
            <TwoFactorCodeInput
              value={googleCode}
              onChange={setGoogleCode}
              disabled={isVerifyingOtp || !otpSent}
            />
          </div>
          {googleError && (
            <div className="text-red-500 text-sm">{googleError}</div>
          )}
          <Button
            type="submit"
            className="w-full"
            disabled={
              isVerifyingOtp || googleCode.trim().length !== 6 || !otpSent
            }
          >
            {isVerifyingOtp ? 'Đang xác thực...' : 'Xác thực'}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => sendOtp()}
            disabled={isSendingOtp || isVerifyingOtp || resendIn > 0}
          >
            {isSendingOtp
              ? 'Đang gửi...'
              : resendIn > 0
              ? `Gửi lại mã (${resendIn}s)`
              : 'Gửi lại mã'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={cancelGoogleLogin}
            disabled={isVerifyingOtp}
          >
            Đăng nhập bằng tài khoản khác
          </Button>
        </form>
      </LoginCard>
    );
  }

  if (step === 'code') {
    return (
      <LoginCard title="Xác thực hai lớp">
        <form onSubmit={handleCodeSubmit} className="space-y-5">
          <p className="text-sm text-gray-600">
            Nhập mã 6 số trong ứng dụng xác thực, hoặc một mã dự phòng.
          </p>
          <div>
            <label className="block mb-1 font-medium">Mã xác thực</label>
            <TwoFactorCodeInput
              value={code}
              onChange={setCode}
              allowBackupCode
              disabled={isVerifying}
            />
          </div>
          {codeError && <div className="text-red-500 text-sm">{codeError}</div>}
          <Button
            type="submit"
            className="w-full"
            disabled={isVerifying || !code.trim()}
          >
            {isVerifying ? 'Đang xác thực...' : 'Xác thực'}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={restart}
            disabled={isVerifying}
          >
            Quay lại
          </Button>
        </form>
      </LoginCard>
    );
  }

  if (step === 'setup') {
    return (
      <LoginCard title="Thiết lập xác thực hai lớp">
        <p className="mb-4 text-sm text-gray-600">
          Vai trò của bạn bắt buộc bật xác thực hai lớp. Hãy thiết lập để tiếp
          tục đăng nhập.
        </p>
        {twoFactorSetup ? (
          <TwoFactorSetupForm
            setup={twoFactorSetup}
            onSubmit={value => {
              setCodeError('');
              enableTwoFactor({ login_challenge: loginChallenge, code: value });
            }}
            isPending={isEnabling}
            error={codeError}
            submitLabel="Bật và đăng nhập"
            onCancel={restart}
          />
        ) : (
          <div className="space-y-4">
            {isStartingSetup ? (
              <div className="text-center text-gray-500">Đang tải...</div>
            ) : (
              <>
                {codeError && (
                  <div className="text-red-500 text-sm">{codeError}</div>
                )}
                <Button
                  type="button"
                  className="w-full"
                  onClick={() => startSetup(loginChallenge)}
                >
                  Thử lại
                </Button>
              </>
            )}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={restart}
              disabled={isStartingSetup}
            >
              Quay lại
            </Button>
          </div>
        )}
      </LoginCard>
    );
  }

  if (step === 'backup-codes' && pendingLogin) {
    return (
      <LoginCard title="Mã dự phòng">
        <BackupCodes
          codes={pendingLogin.backup_codes || []}
          onDone={() => finishLogin(pendingLogin)}
          doneLabel="Tiếp tục đăng nhập"
        />
      </LoginCard>
    );
  }

  return (
    <LoginCard title="Đăng nhập">
      {PASSWORD_LOGIN_ENABLED && (
        <>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block mb-1 font-medium">Email</label>
              <Input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Nhập email của bạn"
                required
              />
            </div>
            <div>
              <label className="block mb-1 font-medium">Mật khẩu</label>
              <PasswordInput
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Nhập mật khẩu của bạn"
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={isPending}>
              {isPending ? 'Đang đăng nhập...' : 'Đăng nhập'}
            </Button>
          </form>

          <div className="my-6 flex items-center justify-center">
            <span className="text-gray-400">hoặc</span>
          </div>
        </>
      )}
      <Button
        disabled={isGoogleLoading}
        size="lg"
        className="w-full"
        onClick={() => login()}
        variant={PASSWORD_LOGIN_ENABLED ? 'outline' : 'default'}
      >
        Đăng nhập với Google 🚀
      </Button>
    </LoginCard>
  );
};

const LoginCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="flex min-h-[70vh] items-center justify-center">
    <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-md">
      <h2 className="mb-6 text-2xl font-bold text-center">{title}</h2>
      {children}
    </div>
  </div>
);

export default AdminLoginPage;
